import React, { useRef, useEffect, useState } from 'react';
import { Button } from '@os/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@os/components/ui/card';
import { Input } from '@os/components/ui/input';
import { Label } from '@os/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@os/components/ui/tabs';
import { Switch } from '@os/components/ui/switch';
import { Slider } from '@os/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@os/components/ui/select';
import { toast } from 'sonner';
import { Upload, Play, Square, Maximize2, Minimize2, Folder, GamepadIcon, Monitor, AlertTriangle, HardDrive, Settings, Activity } from 'lucide-react';
import { globalVFS } from '@os/utils/storage/VFS';
import { useUserVFS } from '@os/hooks/useUserVFS';
import { supabase } from '@os/integrations/supabase/client';
import { useUserSettings } from '@os/hooks/useUserSettings';
import { useEmulationAnalytics } from '@os/hooks/useEmulationAnalytics';
// Declare global Dos function from CDN
declare global {
  interface Window {
    Dos: any;
  }
}

interface DOSEmulatorProps {
  onClose?: () => void;
}

export const DOSEmulator: React.FC<DOSEmulatorProps> = ({ onClose }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dosElementRef = useRef<HTMLDivElement | null>(null);
  const ciRef = useRef<any>(null);
  const lastObjectUrlRef = useRef<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isJsDosLoaded, setIsJsDosLoaded] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dosUrl, setDosUrl] = useState('');
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { getFile } = useUserVFS();

  // Analytics settings: global and per-app
  const { settings: globalAnalytics, updateSetting: updateGlobalAnalytics } = useUserSettings('emulation-analytics', { enabled: false });
  const { settings: appAnalytics, updateSetting: updateAppAnalytics } = useUserSettings('dos-emulator', { analyticsEnabled: false, screenshotsEnabled: false });
  const analyticsEnabled = Boolean(globalAnalytics.enabled) && Boolean(appAnalytics.analyticsEnabled);

  const [currentPath, setCurrentPath] = useState<string>(''); // tracks URL/file path for analytics

  // Shared analytics hook
  const { setContainer, setAppPath, logCustomEvent, captureScreenshot } = useEmulationAnalytics({
    appName: 'DOSEmulator',
    appPath: currentPath,
    enabled: analyticsEnabled,
    screenshotsEnabled: Boolean(appAnalytics.screenshotsEnabled),
    flushIntervalMs: 2000,
    mouseThrottleMs: 400,
  });

  // Point analytics at the DOS canvas container when it's created
  useEffect(() => {
    if (containerRef.current) {
      setContainer(containerRef);
    }
  }, [containerRef.current]);

  // Update analytics path when it changes
  useEffect(() => {
    setAppPath(currentPath);
  }, [currentPath, setAppPath]);

  // DOS Options State
  const [dosOptions, setDosOptions] = useState({
    systemCursor: true,
    captureMouse: true,
    mobileControls: false,
    mirrorControls: false,
    mouseSensitivity: 0.5,
    scaleControls: 0.2,
    volume: 1.0,
    emulation: 'dosbox',
    workerThread: true,
    backgroundRendering: true,
    renderer: 'canvas'
  });

  // Debug: Track isRunning state changes
  useEffect(() => {
    console.log('🔄 isRunning state changed to:', isRunning);
  }, [isRunning]);
  // Load js-dos v8 from CDN (CSS + JS) according to official docs
  useEffect(() => {
    const loadJsDos = () => {
      // If already loaded, don't inject scripts again
      if ((window as any).Dos) {
        setIsJsDosLoaded(true);
        return;
      }

      if (document.getElementById('jsdos-cdn-script')) {
        // Script tag exists but window.Dos not yet ready; wait for onload
        return;
      }

      // Load CSS first
      const css = document.createElement("link");
      css.rel = "stylesheet";
      css.href = "https://v8.js-dos.com/latest/js-dos.css";
      document.head.appendChild(css);

      // Load JS script once
      const script = document.createElement("script");
      script.id = "jsdos-cdn-script";
      script.src = "https://v8.js-dos.com/latest/js-dos.js";
      script.crossOrigin = "anonymous";
      script.onload = () => {
        if (!window.Dos) {
          console.error("❌ js-dos v8 not loaded properly.");
          toast.error("Failed to load js-dos v8. Check your script source.");
          return;
        }
        console.log("✅ js-dos v8 loaded from CDN");
        setIsJsDosLoaded(true);
      };
      script.onerror = () => {
        console.error("❌ Failed to load js-dos from CDN");
        toast.error("Failed to load js-dos from CDN");
      };
      document.head.appendChild(script);
    };

    loadJsDos();
  }, [toast]);

  // Built-in DOS games and applications URLs (real working examples)
  const builtInApps = [
    {
      name: 'QBasic',
      url: 'https://rggfpcqqcktocqarkdsj.supabase.co/storage/v1/object/public/games/user-uploads/1752637949599_QB45.jsdos',
      description: 'QuickBASIC 4.5 programming environment'
    },
    {
      name: 'Digger Game',
      url: 'https://v8.js-dos.com/bundles/digger.jsdos',
      description: 'Classic DOS arcade game (confirmed working)'
    },
    {
      name: 'Doom',
      url: 'https://raw.githubusercontent.com/kaftejiman/jsdos-games/f16f234fcd18b5a6b19f7edb49fa0ceb99c1a0f8/games/doom.jsdos',
      description: 'Classic first-person shooter'
    },
    {
      name: 'GTA',
      url: 'https://raw.githubusercontent.com/kaftejiman/jsdos-games/f16f234fcd18b5a6b19f7edb49fa0ceb99c1a0f8/games/gta.jsdos',
      description: 'Grand Theft Auto classic'
    },
    {
      name: 'Need for Speed',
      url: 'https://raw.githubusercontent.com/kaftejiman/jsdos-games/f16f234fcd18b5a6b19f7edb49fa0ceb99c1a0f8/games/nfs.jsdos',
      description: 'Classic racing game'
    }
  ];

  // Disk images available in js-dos (operating systems)
  const diskImages = [
    {
      name: 'DOS v7.1',
      url: 'https://rggfpcqqcktocqarkdsj.supabase.co/storage/v1/object/public/games/user-uploads/1752620046548_system-dos7.1-v1%20(2).jsdos',
      description: 'MS-DOS 7.1 operating system'
    },
    {
      name: 'Windows 3.11',
      url: '/os/assets/jsdos/system-win311-v1.jsdos',
      description: 'Windows for Workgroups 3.11'
    },
    {
      name: 'Windows 95 v2',
      url: '/os/assets/jsdos/system-win95-v2.jsdos',
      description: 'Windows 95 v2'
    },
    {
      name: 'Windows 98',
      url: '/os/assets/jsdos/system-win98-v1.jsdos',
      description: 'Windows 98 operating system'
    }
  ];

  // Create isolated DOM element for js-dos that React doesn't manage
  useEffect(() => {
    if (containerRef.current && !dosElementRef.current) {
      // Create a completely separate DOM element for js-dos
      const dosElement = document.createElement('div');
      dosElement.style.width = '100%';
      dosElement.style.height = '100%';
      dosElement.style.minHeight = '400px';
      dosElement.style.backgroundColor = 'hsl(var(--background))';
      containerRef.current.appendChild(dosElement);
      dosElementRef.current = dosElement;
    }

    return () => {
      // Cleanup on unmount
      if (ciRef.current) {
        try {
          if (typeof ciRef.current.exit === 'function') {
            ciRef.current.exit();
          } else if (typeof ciRef.current.stop === 'function') {
            ciRef.current.stop();
          } else if (typeof ciRef.current.destroy === 'function') {
            ciRef.current.destroy();
          }
        } catch (error) {
          console.warn('Cleanup error during unmount:', error);
        }
        ciRef.current = null;
      }
      
      // Remove the DOS element from DOM
      if (dosElementRef.current && dosElementRef.current.parentNode) {
        try {
          dosElementRef.current.parentNode.removeChild(dosElementRef.current);
        } catch (error) {
          console.warn('DOM cleanup warning:', error);
        }
        dosElementRef.current = null;
      }
    };
  }, []);

// Helper: ensure we pass a good binary URL to js-dos
  const toBinaryBlobUrl = async (url: string): Promise<string> => {
    // If it's already a blob URL, return as-is
    if (url.startsWith('blob:')) return url;

    // Try fetching as binary; fail fast on non-ok responses
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) {
      throw new Error(`Bundle fetch failed (${res.status})`);
    }
    const buf = await res.arrayBuffer();
    if (!buf || buf.byteLength === 0) {
      throw new Error('Bundle is empty or invalid');
    }
    const blob = new Blob([buf], { type: 'application/octet-stream' });
    const objUrl = URL.createObjectURL(blob);
    return objUrl;
  };

// Main loader: make sure we feed a valid binary blob URL and clean old session
const loadDosApplication = async (url: string) => {
  try {
    if (!dosElementRef.current || !window.Dos) {
      toast.error("DOS emulator not ready.");
      return;
    }

    setCurrentPath(url);
    logCustomEvent('start', { source: 'url', path: url });

    setIsRunning(true);
    toast("Starting DOS emulation...");

    // Cleanup previous session
    if (ciRef.current) {
      try {
        await ciRef.current.exit?.();
      } catch (err) {
        console.warn("Failed to exit previous session:", err);
      }
      ciRef.current = null;
    }
    if (lastObjectUrlRef.current) {
      try { URL.revokeObjectURL(lastObjectUrlRef.current); } catch {}
      lastObjectUrlRef.current = null;
    }

    dosElementRef.current.innerHTML = '';

    const safeUrl = await toBinaryBlobUrl(url);
    lastObjectUrlRef.current = safeUrl;

    const ci = window.Dos(dosElementRef.current, {
      url: safeUrl,
      theme: "dark",
      mouseLock: dosOptions.captureMouse,
      autolock: dosOptions.captureMouse,
    });

    ciRef.current = ci;
    toast.success("DOS app started!");
  } catch (error: any) {
    console.error("Failed to start DOS:", error);
    logCustomEvent('error', { message: error?.message ?? 'Unknown error' });
    setIsRunning(false);
    toast.error(`DOS launch error: ${error.message ?? 'Unknown error'}`);
  }
};


  const loadFromFile = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    try {
      const arrayBuffer = await selectedFile.arrayBuffer();
      const blob = new Blob([arrayBuffer]);
      const url = URL.createObjectURL(blob);
      await loadDosApplication(url);
    } catch (error) {
      console.error('Failed to load file:', error);
      toast.error("Failed to load the selected file");
    }
  };

  const loadFromUrl = async () => {
    if (!dosUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    // Validate URL format
    try {
      new URL(dosUrl.trim());
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    // Check if URL looks like a .jsdos file
    const url = dosUrl.trim();
    if (!url.toLowerCase().endsWith('.jsdos')) {
      toast("Warning: URL should point to a .jsdos bundle file for best compatibility");
    }

    await loadDosApplication(url);
  };

  const loadFromVFS = async (filePath: string) => {
    try {
      console.log('Loading from VFS:', filePath);
      
      // Debug: Check what files exist in global VFS
      try {
        const allFiles = await globalVFS.getAllFiles();
        console.log('All files in global VFS:', allFiles);
        console.log('Type of first file:', typeof allFiles[0]);
        console.log('Number of files found:', allFiles.length);
        
        // Show first 10 file paths to understand the structure
        console.log('First 10 file paths:', allFiles.slice(0, 10).map(f => f.path));
        
        // Look for any files containing "digger" (case insensitive)
        const diggerFiles = allFiles.filter(file => 
          file.path && file.path.toLowerCase().includes('digger')
        );
        console.log('Files containing "digger":', diggerFiles);
        
        // Look for DOS-related files
        const dosFiles = allFiles.filter(file => 
          file.path && (file.path.includes('DOS') || file.path.includes('.jsdos') || file.path.toLowerCase().includes('dos'))
        );
        console.log('DOS-related files:', dosFiles);
        
        // Check exact path matches with different formats
        const exactMatches = allFiles.filter(file => 
          file.path === filePath || 
          file.path === 'C:\\Games\\DOS\\Digger.jsdos' ||
          file.path === 'C:/Games/DOS/Digger.jsdos' ||
          file.path === '/C/Games/DOS/Digger.jsdos'
        );
        console.log('Exact Digger file match:', exactMatches);
        
      } catch (debugError) {
        console.log('Could not get global VFS files:', debugError);
      }
      
      // Try global VFS first (for system files like Digger)
      console.log('Trying global VFS first...');
      
      // DIRECT FIX: If we can't find the DOS games, add them directly
      if (filePath.includes('DOS') && filePath.includes('.jsdos')) {
        const allFiles = await globalVFS.getAllFiles();
        const dosGameExists = allFiles.some(file => 
          file.path && file.path.includes('DOS') && file.path.includes('.jsdos')
        );
        
        if (!dosGameExists) {
          console.log('DOS games not found in VFS, adding them directly...');
          // Add DOS games directly to VFS
          await globalVFS.createDirectory('C:\\Games');
          await globalVFS.createDirectory('C:\\Games\\DOS');
          await globalVFS.save('C:\\Games\\DOS\\Digger.jsdos', 'https://v8.js-dos.com/bundles/digger.jsdos');
          await globalVFS.save('C:\\Games\\DOS\\Prince.jsdos', 'https://v8.js-dos.com/bundles/prince.jsdos');
          await globalVFS.save('C:\\Games\\DOS\\Doom.jsdos', 'https://v8.js-dos.com/bundles/doom.jsdos');
          console.log('DOS games added to VFS');
        }
      }
      console.log('Trying global VFS first...');
      const globalContent = await globalVFS.load(filePath);
      
      if (globalContent) {
        console.log('Global VFS content found, length:', globalContent.length);
        console.log('Content preview:', globalContent.substring(0, 100));
        
        // Check if the content is a URL (common for .jsdos files)
        const trimmedContent = globalContent.trim();
        if (trimmedContent.startsWith('http://') || trimmedContent.startsWith('https://')) {
          console.log('Content is a URL, downloading from:', trimmedContent);
          await loadDosApplication(trimmedContent);
          return;
        }
        
        // Check if content looks like base64
        const isBase64 = /^[A-Za-z0-9+/]+=*$/.test(globalContent);
        console.log('Global content appears to be base64:', isBase64);
        
        if (isBase64) {
          try {
            console.log('Attempting to decode global VFS base64 content...');
            const binaryString = atob(globalContent);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: 'application/zip' });
            const objectUrl = URL.createObjectURL(blob);
            
            console.log('Created object URL from global VFS:', objectUrl);
            await loadDosApplication(objectUrl);
            
            toast.success(`Loaded ${filePath.split('\\').pop()} from VFS`);
            return;
          } catch (decodeError) {
            console.error('Failed to decode global VFS base64 content:', decodeError);
          }
        }
        
        // If not base64, treat as URL
        console.log('Treating global content as URL...');
        await loadDosApplication(globalContent);
        
        toast.success(`Loaded ${filePath.split('\\').pop()} from VFS`);
        return;
      }
      
      // Fallback to user VFS (for uploaded files)
      console.log('Global VFS failed, trying user VFS...');
      const userFile = getFile(filePath);
      console.log('User file found:', userFile ? 'yes' : 'no');
      
      if (userFile && userFile.content) {
        console.log('User file content length:', userFile.content.length);
        console.log('User file content type:', userFile.content_type);
        
        // Check if content looks like base64
        const isBase64 = /^[A-Za-z0-9+/]+=*$/.test(userFile.content);
        console.log('User content appears to be base64:', isBase64);
        
        if (isBase64) {
          try {
            console.log('Attempting to decode user VFS base64 content...');
            const binaryString = atob(userFile.content);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: 'application/zip' });
            const objectUrl = URL.createObjectURL(blob);
            
            console.log('Created object URL from user VFS:', objectUrl);
            await loadDosApplication(objectUrl);
            
            toast.success(`Loaded ${filePath.split('\\').pop()} from VFS`);
            return;
          } catch (decodeError) {
            console.error('Failed to decode user VFS base64 content:', decodeError);
          }
        }
        
        // If not base64, try as URL
        console.log('Treating user content as URL...');
        await loadDosApplication(userFile.content);
        
        toast.success(`Loaded ${filePath.split('\\').pop()} from VFS`);
        return;
      }

      // No file found in either VFS
      toast.error("Could not load file from VFS. Make sure to upload the .jsdos file to your file system first.");
    } catch (error) {
      console.error('Error loading from VFS:', error);
      toast.error("Failed to load game from VFS");
    }
  };

  const stopEmulation = async () => {
    if (ciRef.current) {
      try {
        if (typeof ciRef.current.exit === 'function') {
          await ciRef.current.exit();
        } else if (typeof ciRef.current.stop === 'function') {
          await ciRef.current.stop();
        } else if (typeof ciRef.current.destroy === 'function') {
          await ciRef.current.destroy();
        }
        ciRef.current = null;
        setIsRunning(false);

        if (dosElementRef.current) {
          dosElementRef.current.innerHTML = '';
          dosElementRef.current.style.backgroundColor = 'hsl(var(--background))';
        }

        if (lastObjectUrlRef.current) {
          try { URL.revokeObjectURL(lastObjectUrlRef.current); } catch {}
          lastObjectUrlRef.current = null;
        }

        logCustomEvent('stop', { path: currentPath });
        toast("DOS emulation stopped");
      } catch (error) {
        console.error('Failed to stop emulation:', error);
        logCustomEvent('error', { message: (error as any)?.message ?? 'stop_failed' });
        ciRef.current = null;
        setIsRunning(false);
        if (dosElementRef.current) {
          dosElementRef.current.innerHTML = '';
          dosElementRef.current.style.backgroundColor = 'hsl(var(--background))';
        }
        if (lastObjectUrlRef.current) {
          try { URL.revokeObjectURL(lastObjectUrlRef.current); } catch {}
          lastObjectUrlRef.current = null;
        }
      }
    }
  };

  const toggleFullscreen = () => {
    if (!dosElementRef.current) return;

    if (!isFullscreen) {
      dosElementRef.current.requestFullscreen?.() ||
      (dosElementRef.current as any).webkitRequestFullscreen?.() ||
      (dosElementRef.current as any).mozRequestFullScreen?.();
      logCustomEvent('fullscreen_enter');
    } else {
      document.exitFullscreen?.() ||
      (document as any).webkitExitFullscreen?.() ||
      (document as any).mozCancelFullScreen?.();
      logCustomEvent('fullscreen_exit');
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.name.endsWith('.jsdos')) {
      console.log(`Selected file: ${file.name}, size: ${file.size} bytes`);
      setSelectedFile(file);
      
      // Upload to Supabase storage
      try {
        console.log('Uploading file to Supabase storage...');
        const fileName = `${Date.now()}_${file.name}`;
        const filePath = `user-uploads/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('games')
          .upload(filePath, file);
          
        if (uploadError) {
          console.error('Upload error:', uploadError);
        toast.error(`Upload failed: ${uploadError.message}`);
          return;
        }
        
        // Get public URL
        const { data: urlData } = supabase.storage
          .from('games')
          .getPublicUrl(filePath);
          
        const publicUrl = urlData.publicUrl;
        console.log('File uploaded successfully, public URL:', publicUrl);
        
        // Save to VFS as well for integration
        const vfsPath = `C:\\Games\\DOS\\${file.name}`;
        await globalVFS.save(vfsPath, publicUrl);
        console.log('File saved to VFS:', vfsPath);
        
        toast.success(`File uploaded: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
      } catch (error) {
        console.error('File upload failed:', error);
        toast.error('File upload failed');
      }
    } else {
      toast.error('Please select a .jsdos file');
    }
  };

  // Add error handling for React errors
  useEffect(() => {
    const handleError = (error: ErrorEvent) => {
      // Cross-origin worker errors often show "Script error."
      console.error('DOSEmulator error:', error);
    };
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // If there's an error, show error UI
  if (hasError) {
    return (
      <div className="h-full flex flex-col bg-background">
        <div className="border-b border-border p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <h1 className="text-lg font-semibold">DOS Emulator - Error</h1>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <AlertTriangle className="h-16 w-16 mx-auto text-destructive" />
            <h2 className="text-xl font-semibold">Emulator Error</h2>
            <p className="text-muted-foreground max-w-md">{errorMessage}</p>
            <Button 
              onClick={() => {
                setHasError(false);
                setErrorMessage('');
                window.location.reload();
              }}
              className="mt-4"
            >
              Restart Emulator
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Monitor className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">DOS Emulator</h1>
          </div>
          <div className="flex items-center gap-2">
            {isRunning && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleFullscreen}
                  className="flex items-center gap-2"
                >
                  {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen (F11 to Esc)'}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={stopEmulation}
                  className="flex items-center gap-2"
                >
                  <Square className="h-4 w-4" />
                  Stop
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Sidebar - only show when not running or js-dos not loaded */}
        {(!isRunning || !isJsDosLoaded) && (
          <div className="w-96 border-r border-border p-6 overflow-y-auto">
            {!isJsDosLoaded ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm text-muted-foreground">Loading DOS emulator...</p>
                </div>
              </div>
            ) : (
            <Tabs defaultValue="builtin" className="w-full h-full">
              <TabsList className="grid w-full grid-cols-6 h-12">
                <TabsTrigger value="builtin" className="text-sm">Apps</TabsTrigger>
                <TabsTrigger value="os" className="text-sm">OS</TabsTrigger>
                <TabsTrigger value="vfs" className="text-sm">VFS</TabsTrigger>
                <TabsTrigger value="file" className="text-sm">File</TabsTrigger>
                <TabsTrigger value="url" className="text-sm">URL</TabsTrigger>
                <TabsTrigger value="options" className="text-sm">Options</TabsTrigger>
              </TabsList>

              <TabsContent value="builtin" className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <GamepadIcon className="h-4 w-4" />
                    Demo Applications (Abandonware)
                  </h3>
                  <div className="space-y-2">
                    {builtInApps.map((app, index) => (
                      <Card key={index} className="cursor-pointer hover:bg-accent/50 transition-colors border-0">
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-medium">{app.name}</h4>
                              <p className="text-xs text-muted-foreground">{app.description}</p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => loadDosApplication(app.url)}
                              className="ml-2"
                              disabled={!isJsDosLoaded || isRunning}
                              variant={isRunning ? "secondary" : "default"}
                            >
                              {isRunning ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                    <h4 className="text-sm font-medium mb-2">💡 How to get DOS applications:</h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• Upload .jsdos files (bundled DOS apps)</li>
                      <li>• Upload .zip files containing DOS games</li>
                      <li>• Try URLs from archive.org or other DOS sites</li>
                      <li>• Visit js-dos.com for more examples</li>
                    </ul>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="os" className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Monitor className="h-4 w-4" />
                    Operating System Images
                  </h3>
                  <div className="space-y-2">
                    {diskImages.map((os, index) => (
                      <Card key={index} className="cursor-pointer hover:bg-accent/50 transition-colors border-0">
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-medium">{os.name}</h4>
                              <p className="text-xs text-muted-foreground">{os.description}</p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => loadDosApplication(os.url)}
                              className="ml-2"
                              disabled={!isJsDosLoaded || isRunning}
                              variant={isRunning ? "secondary" : "default"}
                            >
                              {isRunning ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                    <h4 className="text-sm font-medium mb-2">💻 OS Images:</h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• Boot directly into classic operating systems</li>
                      <li>• Experience DOS, Windows 3.x, and Windows 9x</li>
                      <li>• Run period-appropriate software and games</li>
                      <li>• Multiple language versions available</li>
                    </ul>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="vfs" className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <HardDrive className="h-4 w-4" />
                    VFS Games (C:\Games\DOS\)
                  </h3>
                  <div className="space-y-2">
                    <Card className="cursor-pointer hover:bg-accent/50 transition-colors border-0">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="text-sm font-medium">Digger.jsdos</h4>
                            <p className="text-xs text-muted-foreground">Classic DOS arcade game (from VFS)</p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => loadFromVFS('C:\\Games\\DOS\\Digger.jsdos')}
                            className="ml-2"
                            disabled={!isJsDosLoaded || isRunning}
                            variant={isRunning ? "secondary" : "default"}
                          >
                            {isRunning ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:bg-accent/50 transition-colors border-0">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="text-sm font-medium">Prince.jsdos</h4>
                            <p className="text-xs text-muted-foreground">Prince of Persia (from VFS)</p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => loadFromVFS('C:\\Games\\DOS\\Prince.jsdos')}
                            className="ml-2"
                            disabled={!isJsDosLoaded || isRunning}
                            variant={isRunning ? "secondary" : "default"}
                          >
                            {isRunning ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:bg-accent/50 transition-colors border-0">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="text-sm font-medium">Doom.jsdos</h4>
                            <p className="text-xs text-muted-foreground">Legendary FPS game (from VFS)</p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => loadFromVFS('C:\\Games\\DOS\\Doom.jsdos')}
                            className="ml-2"
                            disabled={!isJsDosLoaded || isRunning}
                            variant={isRunning ? "secondary" : "default"}
                          >
                            {isRunning ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                    <h4 className="text-sm font-medium mb-2">📂 VFS Integration:</h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• Games stored in C:\Games\DOS\ directory</li>
                      <li>• Use File Explorer to upload more .jsdos files</li>
                      <li>• Files persist between sessions</li>
                      <li>• Access via virtual file system</li>
                    </ul>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="file" className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Folder className="h-4 w-4" />
                    Load from File
                  </h3>
                  <Card className="border-0">
                    <CardContent className="p-4 space-y-4">
                      <div>
                        <Label htmlFor="dosFile" className="text-sm font-medium">
                          Select DOS Application (.jsdos, .zip)
                        </Label>
                        <Input
                          id="dosFile"
                          type="file"
                          accept=".jsdos,.zip"
                          onChange={handleFileSelect}
                          className="mt-1"
                        />
                      </div>
                      {selectedFile && (
                        <div className="text-xs text-muted-foreground">
                          Selected: {selectedFile.name}
                        </div>
                      )}
                      <Button
                        onClick={loadFromFile}
                        disabled={!selectedFile || !isJsDosLoaded}
                        className="w-full flex items-center gap-2"
                      >
                        <Upload className="h-4 w-4" />
                        Load Application
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="url" className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium mb-3">Load from URL</h3>
                  <Card className="border-0">
                    <CardContent className="p-4 space-y-4">
                      <div>
                        <Label htmlFor="dosUrl" className="text-sm font-medium">
                          DOS Application URL
                        </Label>
                        <Input
                          id="dosUrl"
                          type="url"
                          placeholder="https://example.com/game.jsdos"
                          value={dosUrl}
                          onChange={(e) => setDosUrl(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <Button
                        onClick={loadFromUrl}
                        disabled={!dosUrl.trim() || !isJsDosLoaded}
                        className="w-full flex items-center gap-2"
                      >
                         <Play className="h-4 w-4" />
                         Load from URL
                       </Button>
                     </CardContent>
                   </Card>
                 </div>
                </TabsContent>

                <TabsContent value="options" className="space-y-6 h-full overflow-y-auto pb-6">
                  <div>
                    <h3 className="text-base font-medium mb-6 flex items-center gap-3">
                      <Settings className="h-5 w-5" />
                      Emulator Options
                    </h3>
                    
                    <div className="space-y-8">
                      {/* Controls Section */}
                      <Card className="border-0">
                        <CardHeader className="pb-6">
                          <CardTitle className="text-base flex items-center gap-2">
                            <GamepadIcon className="h-5 w-5" />
                            Controls
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          <div className="flex items-center justify-between py-2">
                            <Label htmlFor="system-cursor" className="text-base">System cursor</Label>
                            <Switch 
                              id="system-cursor"
                              checked={dosOptions.systemCursor}
                              onCheckedChange={(checked) => setDosOptions(prev => ({ ...prev, systemCursor: checked }))}
                            />
                          </div>
                          
                          <div className="flex items-center justify-between py-2">
                            <Label htmlFor="capture-mouse" className="text-base">Capture mouse</Label>
                            <Switch 
                              id="capture-mouse"
                              checked={dosOptions.captureMouse}
                              onCheckedChange={(checked) => setDosOptions(prev => ({ ...prev, captureMouse: checked }))}
                            />
                          </div>
                          
                          <div className="flex items-center justify-between py-2">
                            <Label htmlFor="mobile-controls" className="text-base">Mobile controls</Label>
                            <Switch 
                              id="mobile-controls"
                              checked={dosOptions.mobileControls}
                              onCheckedChange={(checked) => setDosOptions(prev => ({ ...prev, mobileControls: checked }))}
                            />
                          </div>
                          
                          <div className="space-y-4 pt-4">
                            <div className="flex items-center justify-between">
                              <Label className="text-base">Mouse sensitivity</Label>
                              <span className="text-base text-muted-foreground font-mono">{dosOptions.mouseSensitivity.toFixed(2)}</span>
                            </div>
                            <Slider
                              value={[dosOptions.mouseSensitivity]}
                              onValueChange={([value]) => setDosOptions(prev => ({ ...prev, mouseSensitivity: value }))}
                              max={2}
                              min={0.1}
                              step={0.01}
                              className="w-full"
                            />
                          </div>
                          
                          <div className="space-y-4 pt-4">
                            <div className="flex items-center justify-between">
                              <Label className="text-base">Scale controls</Label>
                              <span className="text-base text-muted-foreground font-mono">{dosOptions.scaleControls.toFixed(2)}</span>
                            </div>
                            <Slider
                              value={[dosOptions.scaleControls]}
                              onValueChange={([value]) => setDosOptions(prev => ({ ...prev, scaleControls: value }))}
                              max={2}
                              min={0.1}
                              step={0.01}
                              className="w-full"
                            />
                          </div>
                          
                          <div className="space-y-4 pt-4">
                            <div className="flex items-center justify-between">
                              <Label className="text-base">Volume</Label>
                              <span className="text-base text-muted-foreground font-mono">{dosOptions.volume.toFixed(2)}</span>
                            </div>
                            <Slider
                              value={[dosOptions.volume]}
                              onValueChange={([value]) => setDosOptions(prev => ({ ...prev, volume: value }))}
                              max={1}
                              min={0}
                              step={0.01}
                              className="w-full"
                            />
                          </div>
                        </CardContent>
                      </Card>

                      {/* Emulation Section */}
                      <Card className="border-0">
                        <CardHeader className="pb-6">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Monitor className="h-5 w-5" />
                            Emulation
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          <div className="space-y-3">
                            <Label className="text-base">Emulation Engine</Label>
                            <Select 
                              value={dosOptions.emulation} 
                              onValueChange={(value) => setDosOptions(prev => ({ ...prev, emulation: value }))}
                            >
                              <SelectTrigger className="h-11 text-base">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="dosbox">DOSBox</SelectItem>
                                <SelectItem value="jsdos">js-dos</SelectItem>
                                <SelectItem value="emscripten">Emscripten</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="flex items-center justify-between py-2">
                            <Label htmlFor="worker-thread" className="text-base">Worker thread</Label>
                            <Switch 
                              id="worker-thread"
                              checked={dosOptions.workerThread}
                              onCheckedChange={(checked) => setDosOptions(prev => ({ ...prev, workerThread: checked }))}
                            />
                          </div>
                          
                          <div className="flex items-center justify-between py-2">
                            <Label htmlFor="background-rendering" className="text-base">Background rendering</Label>
                            <Switch 
                              id="background-rendering"
                              checked={dosOptions.backgroundRendering}
                              onCheckedChange={(checked) => setDosOptions(prev => ({ ...prev, backgroundRendering: checked }))}
                            />
                          </div>
                          
                          <div className="space-y-3 pt-4">
                            <Label className="text-base">Renderer</Label>
                            <Select 
                              value={dosOptions.renderer} 
                              onValueChange={(value) => setDosOptions(prev => ({ ...prev, renderer: value }))}
                            >
                              <SelectTrigger className="h-11 text-base">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="canvas">Canvas</SelectItem>
                                <SelectItem value="webgl">WebGL</SelectItem>
                                <SelectItem value="webgl2">WebGL2</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </CardContent>
                      </Card>
                      
                      {/* Quick Actions */}
                      <Card className="border-0">
                        <CardHeader className="pb-6">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Settings className="h-5 w-5" />
                            Quick Presets
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 gap-4">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setDosOptions({
                                systemCursor: true,
                                captureMouse: true,
                                mobileControls: false,
                                mirrorControls: false,
                                mouseSensitivity: 0.5,
                                scaleControls: 0.2,
                                volume: 1.0,
                                emulation: 'dosbox',
                                workerThread: true,
                                backgroundRendering: true,
                                renderer: 'canvas'
                              })}
                              className="text-sm justify-start h-9"
                            >
                              🔄 Reset to Default
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setDosOptions(prev => ({ 
                                ...prev, 
                                volume: 0,
                                captureMouse: false,
                                systemCursor: false 
                              }))}
                              className="text-sm justify-start h-9"
                            >
                              🔇 Silent Mode
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setDosOptions(prev => ({ 
                                ...prev,
                                mobileControls: true,
                                scaleControls: 0.8,
                                mouseSensitivity: 0.3
                              }))}
                              className="text-sm justify-start h-9"
                            >
                              📱 Mobile Optimized
                            </Button>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Analytics section */}
                      <Card className="border-0">
                        <CardHeader className="pb-4">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Activity className="h-5 w-5" />
                            Emulation Analytics
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="flex items-center justify-between">
                            <Label className="text-base">Share usage data to improve AI</Label>
                            <Switch
                              checked={Boolean(appAnalytics.analyticsEnabled)}
                              onCheckedChange={(checked) => updateAppAnalytics('analyticsEnabled', checked)}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Global toggle is in AI Center. Data includes user id, app name/path, and input activity.
                          </p>
                          <div className="flex items-center justify-between">
                            <Label className="text-base">Capture screenshots (experimental)</Label>
                            <Switch
                              checked={Boolean(appAnalytics.screenshotsEnabled)}
                              onCheckedChange={(checked) => updateAppAnalytics('screenshotsEnabled', checked)}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => captureScreenshot()}
                              disabled={!analyticsEnabled || !appAnalytics.screenshotsEnabled}
                            >
                              Capture Screenshot Now
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
              )}
           </div>
        )}

        <div className="flex-1 bg-background relative overflow-hidden">
          {!isRunning && (
            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground z-10">
              <div className="text-center">
                <Monitor className="h-16 w-16 mx-auto mb-4 opacity-50" />
                {!isJsDosLoaded ? (
                  <>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-lg">Loading DOS emulator...</p>
                    <p className="text-sm mt-2">Please wait while we initialize the emulator</p>
                  </>
                ) : (
                  <>
                    <p className="text-lg">Select a DOS application to start emulation</p>
                    <p className="text-sm mt-2">Choose from built-in apps, upload a file, or load from URL</p>
                  </>
                )}
              </div>
            </div>
          )}
          <div
            ref={containerRef}
            className="absolute inset-0 w-full h-full bg-background"
            style={{ 
              zIndex: isRunning ? 100 : 1,
              minHeight: '400px',
              cursor: isRunning ? 'none' : 'default'
            }}
            suppressContentEditableWarning={true}
            suppressHydrationWarning={true}
          />
        </div>
      </div>
    </div>
  );
};